/**
 * useClerkOrgSync — idempotent Clerk org name sync after /profiel PATCH.
 *
 * Behaviour:
 * - Calls organization.update({ name }) after a successful API save.
 * - Transient Clerk errors are swallowed (logged to console + Sentry) so the
 *   API save is never rolled back.
 * - No-ops when the user has no active organization.
 */

import { useOrganization } from '@clerk/react';
import * as Sentry from '@sentry/react';

export interface ClerkOrgSyncParams {
  /** New organization name (companyName). Empty string is skipped. */
  name: string;
}

export interface UseClerkOrgSyncReturn {
  /**
   * Attempt to sync the Clerk org. Always resolves — never rejects.
   * Returns `true` if the Clerk update succeeded, `false` if it was skipped or
   * failed transiently.
   */
  syncOrg: (params: ClerkOrgSyncParams) => Promise<boolean>;
}

export function useClerkOrgSync(): UseClerkOrgSyncReturn {
  const { organization } = useOrganization();

  const syncOrg = async ({ name }: ClerkOrgSyncParams): Promise<boolean> => {
    if (!organization) return false;
    const trimmed = name.trim();
    if (!trimmed) return false;
    // Skip if already in sync to avoid unnecessary API round-trips.
    if (organization.name === trimmed) return true;

    try {
      await organization.update({ name: trimmed });
      return true;
    } catch (err: unknown) {
      // Transient Clerk failure — do not surface to user, do not block save.
      console.warn('[useClerkOrgSync] Clerk org update failed (transient):', err);
      Sentry.captureException(err, {
        tags: { source: 'clerk-org-sync' },
        level: 'warning',
      });
      return false;
    }
  };

  return { syncOrg };
}
